import type { ReactNode } from 'react';

import styles from './Table.module.css';

export interface TableColumn<Row> {
  readonly id: string;
  readonly header: ReactNode;
  /** Cell renderer. Kept a pure projection — no data access here (§5.4). */
  readonly cell: (row: Row) => ReactNode;
  /** Right-aligned, tabular, LTR digits. Use for amounts, counts, and IDs. */
  readonly numeric?: boolean;
}

export interface TableProps<Row> {
  /**
   * Required. A table without a caption gives screen-reader users no way to
   * know what they have landed in (§8.6).
   */
  readonly caption: ReactNode;
  /** Hides the caption visually while keeping it for assistive tech. */
  readonly hideCaption?: boolean;
  readonly columns: readonly TableColumn<Row>[];
  readonly rows: readonly Row[];
  readonly rowKey: (row: Row) => string;
  readonly compact?: boolean;
  /** §8.9 / §13.1 — loading, empty, and error are distinct states. */
  readonly loading?: boolean;
  readonly emptyMessage?: ReactNode;
  readonly skeletonRows?: number;
}

/**
 * Dense data table — design addon §8.6.
 *
 * Renders real `<table>` semantics with `<th scope="col">` rather than a grid of
 * divs, so row and column relationships survive for assistive technology.
 */
export function Table<Row>({
  caption,
  hideCaption = false,
  columns,
  rows,
  rowKey,
  compact = false,
  loading = false,
  emptyMessage = 'لا توجد بيانات لعرضها.',
  skeletonRows = 5,
}: TableProps<Row>) {
  const showEmpty = !loading && rows.length === 0;

  return (
    <div className={styles.scroller}>
      <table
        className={[styles.table, compact ? styles.compact : undefined].filter(Boolean).join(' ')}
        aria-busy={loading || undefined}
      >
        <caption className={hideCaption ? 'visually-hidden' : styles.caption}>{caption}</caption>

        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.id}
                scope="col"
                className={[styles.headerCell, column.numeric ? styles.numeric : undefined]
                  .filter(Boolean)
                  .join(' ')}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {loading
            ? Array.from({ length: skeletonRows }, (_, index) => (
                <tr key={`skeleton-${String(index)}`} className={styles.skeletonRow}>
                  {columns.map((column) => (
                    <td key={column.id} className={styles.cell}>
                      <div className={styles.skeletonBar} />
                    </td>
                  ))}
                </tr>
              ))
            : rows.map((row) => (
                <tr key={rowKey(row)} className={styles.row}>
                  {columns.map((column) => (
                    <td
                      key={column.id}
                      className={[styles.cell, column.numeric ? styles.numeric : undefined]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {column.cell(row)}
                    </td>
                  ))}
                </tr>
              ))}

          {showEmpty ? (
            <tr>
              <td className={styles.stateCell} colSpan={columns.length}>
                {emptyMessage}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}
